import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import CardGrid, {Card} from 'analysis-tool/ui/components/CardGrid';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHTER, BLUE, BLUE_POWDER_LIGHT} from 'ui-library/Colors';
import removeNulls from 'util/removeNulls';

interface BaseJoinTypeCardGridProps<T extends string> {
  /** The selected value */
  value: T | null;
  /** Callback, when a new value is selected. */
  onSelect: (newValue: T) => void;
  /** Order in which values' cards are displayed. */
  orderedValues: ReadonlyArray<T>;
  /** Map of values to card titles. */
  valueToTitle: Record<T, string>;
  /** Map of values to descriptions. */
  valueToDescription: Record<T, string>;
  /** Map of values to images URLs, to show within cards. */
  valueToImageUrl: Record<T, string>;
  disabled?: boolean;
}

/**
 * Base component of `JoinTypeSelector`, shared with `CombineTablesMethodSelector` for uniform styling.
 *
 * @package
 */
export default function BaseJoinTypeCardGrid<T extends string>({
  value,
  onSelect,
  orderedValues,
  valueToTitle,
  valueToDescription,
  valueToImageUrl,
  disabled,
}: BaseJoinTypeCardGridProps<T>): JSX.Element {
  return (
    <CardGrid minWidth="225px">
      {orderedValues.map((valueToSelect) => (
        <BaseJoinTypeCard
          key={valueToSelect}
          description={valueToDescription[valueToSelect]}
          disabled={disabled}
          imageUrl={valueToImageUrl[valueToSelect]}
          isSelected={value === valueToSelect}
          onSelect={() => {
            onSelect(valueToSelect);
          }}
          title={valueToTitle[valueToSelect]}
          value={valueToSelect}
        />
      ))}
    </CardGrid>
  );
}

/** @package */
export interface BaseJoinTypeCardProps<T extends string> {
  value: T;
  onSelect: () => void;
  isSelected: boolean;
  title: string;
  description: string;
  imageUrl: string;
  disabled?: boolean;
}

/** @pacakge */
export function BaseJoinTypeCard<T extends string>({
  value,
  onSelect,
  isSelected,
  title,
  description,
  imageUrl,
  disabled,
}: BaseJoinTypeCardProps<T>): JSX.Element {
  return (
    <Card
      icon={<img className={css(styles.iconContainer)} src={imageUrl} />}
      title={title}
      description={description}
      containerProps={{
        styles: removeNulls([
          disabled ? null : styles.cardButtonSelectable,
          isSelected ? styles.cardButton___Active : null,
        ]),
        onClick: disabled ? noop : onSelect,
        ...getTestProps({element: 'button', key: value}),
      }}
    />
  );
}

export const styles = StyleSheet.create({
  cardButtonSelectable: {
    cursor: 'pointer',
    ':hover': {
      background: GRAY_LIGHTER,
    },
  },
  cardButton___Active: {
    borderColor: BLUE,
    backgroundColor: BLUE_POWDER_LIGHT,
  },
  iconContainer: {
    objectFit: 'contain',
  },
});
